import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Sparkles } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';
import SEO from '@/components/SEO';
import SocialStrip from '@/components/SocialStrip';

const TABS = [
  { key: 'all', tkey: 'toprated.typeAll', fallback: 'All' },
  { key: 'product', tkey: 'toprated.typeProducts', fallback: 'Products' },
  { key: 'design', tkey: 'toprated.typeDesigns', fallback: 'Designs' },
];

const CATEGORY_TABS = [
  { key: 'all', tkey: 'toprated.allCategories', fallback: 'All Categories' },
  { key: 'phone_case', tkey: 'shop.phoneCases', fallback: 'Phone Cases' },
  { key: 'sticker', tkey: 'shop.stickers', fallback: 'Stickers' },
  { key: 'label', tkey: 'shop.labels', fallback: 'Labels' },
];

const rankBadge = (idx) => {
  if (idx === 0) return { color: 'from-amber-400 to-yellow-500', label: '#1' };
  if (idx === 1) return { color: 'from-slate-300 to-slate-500', label: '#2' };
  if (idx === 2) return { color: 'from-orange-400 to-orange-700', label: '#3' };
  return { color: 'from-white/10 to-white/20', label: `#${idx + 1}` };
};

const TopRated = () => {
  const [type, setType] = useState('all');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const { format } = useCurrency();
  const { t } = useLanguage();

  useEffect(() => {
    setLoading(true);
    const params = { limit: 36, min_ratings: 1 };
    if (type !== 'all') params.entity_type = type;
    if (category !== 'all') params.category = category;
    api.get('/top-rated', { params })
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, [type, category]);

  const summary = useMemo(() => {
    if (!items.length) return null;
    const total = items.reduce((s, i) => s + i.rating_count, 0);
    const avg = items.reduce((s, i) => s + i.rating_avg, 0) / items.length;
    return { total, avg: Math.round(avg * 10) / 10, count: items.length };
  }, [items]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <SEO
        title="Top Rated — Best Custom Prints & Designs on Jwalek"
        description="Discover the highest-rated custom phone cases, stickers, labels, and design templates on Jwalek — voted for by real customers."
        path="/top-rated"
      />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-2xl grid place-items-center jw-gradient-bg text-white">
          <Trophy className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{t('toprated.title', 'Top Rated')}</h1>
          <p style={{ color: 'var(--jw-text-muted)' }}>{t('toprated.subtitle', "The community's favourite products and designs — ranked by real ratings.")}</p>
        </div>
      </div>

      {summary && (
        <div className="grid sm:grid-cols-3 gap-3 mt-6">
          <Card className="jw-card p-4" data-testid="toprated-stat-avg">
            <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('toprated.avgRating', 'Average rating')}</p>
            <p className="text-2xl font-semibold mt-1">{summary.avg} <span className="text-sm" style={{ color: 'var(--jw-text-subtle)' }}>/ 5</span></p>
          </Card>
          <Card className="jw-card p-4" data-testid="toprated-stat-count">
            <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('toprated.rankedItems', 'Ranked items')}</p>
            <p className="text-2xl font-semibold mt-1">{summary.count}</p>
          </Card>
          <Card className="jw-card p-4" data-testid="toprated-stat-total">
            <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('toprated.totalRatings', 'Total ratings')}</p>
            <p className="text-2xl font-semibold mt-1">{summary.total.toLocaleString()}</p>
          </Card>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="-mx-4 sm:mx-0 overflow-x-auto jw-scrollbar">
          <div className="px-4 sm:px-0 min-w-max">
            <Tabs value={type} onValueChange={setType}>
              <TabsList>
                {TABS.map((tab) => (
                  <TabsTrigger key={tab.key} value={tab.key} data-testid={`toprated-type-${tab.key}`}>{t(tab.tkey, tab.fallback)}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>
        <div className="-mx-4 sm:mx-0 overflow-x-auto jw-scrollbar">
          <div className="px-4 sm:px-0 min-w-max">
            <Tabs value={category} onValueChange={setCategory}>
              <TabsList>
                {CATEGORY_TABS.map((c) => (
                  <TabsTrigger key={c.key} value={c.key} data-testid={`toprated-category-${c.key}`}>{t(c.tkey, c.fallback)}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-8" data-testid="toprated-loading">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="jw-card p-3">
              <Skeleton className="aspect-square rounded-md" />
              <Skeleton className="h-4 mt-3 w-3/4" />
              <Skeleton className="h-3 mt-2 w-1/2" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="jw-card p-16 text-center mt-8" data-testid="toprated-empty">
          <Sparkles className="w-8 h-8 mx-auto" style={{ color: 'var(--jw-text-subtle)' }} />
          <p className="mt-3" style={{ color: 'var(--jw-text-muted)' }}>
            {t('toprated.empty', 'No ratings yet in this view. Be the first to rate a product or design!')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
          {items.map((it, idx) => {
            const badge = rankBadge(idx);
            const href = it.entity_type === 'product' ? `/product/${it.id}` : `/designs/${it.id}`;
            const img = (it.entity_type === 'product' ? it.image_url : assetUrl(it.image_url)) || '/favicon.svg';
            return (
              <Card key={`${it.entity_type}-${it.id}`} className="jw-card overflow-hidden group flex flex-col" data-testid={`toprated-item-${it.entity_type}-${it.id}`}>
                <div className="relative">
                  <Link to={href}>
                    <div className="aspect-square overflow-hidden p-2" style={{ background: 'var(--jw-bg-elev)' }}>
                      <img src={img} alt={it.title} onError={(e) => { e.currentTarget.src = '/favicon.svg'; }} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  </Link>
                  <div className={`absolute top-2 left-2 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white bg-gradient-to-br ${badge.color} shadow-lg`}>
                    {badge.label}
                  </div>
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-black/55 border-white/10 text-white/90 text-[10px] capitalize">
                      {it.entity_type === 'product' ? t('toprated.badgeProduct', 'Product') : t('toprated.badgeDesign', 'Design')}
                    </Badge>
                  </div>
                </div>
                <div className="p-3 flex flex-col gap-2 flex-1">
                  <Link to={href} className="block">
                    <h3 className="font-semibold text-sm truncate">{it.title}</h3>
                    <p className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>
                      {it.category ? t(`designCategory.${it.category}`, it.category.replace('_', ' ')) : ''}
                      {it.price ? <span> · {format(it.price)}</span> : null}
                    </p>
                  </Link>
                  <SocialStrip
                    entityType={it.entity_type}
                    entityId={it.id}
                    initial={{
                      liked: it.liked, saved: it.saved,
                      likes: it.likes, saves: it.saves,
                      rating_avg: it.rating_avg, rating_count: it.rating_count,
                      my_rating: it.my_rating,
                    }}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TopRated;
